/**
 * Cart Line Item Properties
 *
 * Intercepts /cart/add.js and /cart/change.js to manage line item properties:
 *  - On add: attaches Details (square-foot summary), Dimensions, and Part #
 *    for length-and-type products.
 *  - On change: refreshes Details for the new quantity while resending the
 *    line's existing properties, so nothing (Lead time, Dimensions, Part #,
 *    etc.) is dropped.
 */

(function() {
  'use strict';

  /**
   * Format cents as currency string
   */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Parse board length from option2 string (e.g., "6'" -> 6)
   */
  function parseBoardLength(boardLengthStr) {
    if (!boardLengthStr) return 0;
    const cleaned = boardLengthStr.replace(/[''′]/g, '').trim();
    return parseFloat(cleaned) || 0;
  }

  /**
   * Parse price from data attribute (e.g., "$281.37 USD" -> 28137 cents)
   */
  function parsePrice(priceStr) {
    if (!priceStr) return 0;
    const match = priceStr.match(/[\d,.]+/);
    if (!match) return 0;
    const dollars = parseFloat(match[0].replace(/,/g, '')) || 0;
    return Math.round(dollars * 100);
  }

  /**
   * Calculate the sqft info string
   */
  function calculateSqftString(quantity, boardLength, boardsPerUnit, sqftPerLf, soldPerBox, unitPriceCents) {
    if (sqftPerLf === 0 || boardLength === 0) {
      return null;
    }

    const totalBoards = quantity * boardsPerUnit;
    const totalLinearFeet = totalBoards * boardLength;
    const totalSqFt = totalLinearFeet * sqftPerLf;
    const linePriceCents = unitPriceCents * quantity;
    const costPerSqFtCents = totalSqFt > 0 ? (linePriceCents / totalSqFt) : 0;

    const unitLabel = soldPerBox
      ? (quantity === 1 ? 'box' : 'boxes')
      : (quantity === 1 ? 'board' : 'boards');

    return `${quantity} ${unitLabel}: ${totalSqFt.toFixed(2)} sq. ft. @ ${formatMoney(costPerSqFtCents)}/sq. ft.`;
  }

  // ============================================
  // ADD TO CART: Get data from .hidden-variant-info on product page
  // ============================================

  function getVariantDataFromProductPage(variantId) {
    const el = document.querySelector(`.hidden-variant-info[data-variant-id="${variantId}"]`);
    if (!el) {
      return null;
    }

    const hasLengthAndTypeVariants = el.dataset.hasLengthAndTypeVariants === 'true';
    if (!hasLengthAndTypeVariants) {
      return null;
    }

    return {
      boardLength: parseBoardLength(el.dataset.boardLength),
      boardsPerUnit: parseInt(el.dataset.boardsPerUnit, 10) || 1,
      sqftPerLf: parseFloat(el.dataset.sqftPerLf) || 0,
      soldPerBox: el.dataset.soldPerBox === 'true',
      unitPriceCents: parsePrice(el.dataset.price),
      dimensions: (el.dataset.dimensions || '').trim(),
      partNumber: (el.dataset.partNumber || '').trim()
    };
  }

  // ============================================
  // CART CHANGE: Get data from .line-item__sqft-info in cart
  // ============================================

  function getSqftDataFromCart(lineKey) {
    const el = document.querySelector(`.line-item__sqft-info[data-line-key="${lineKey}"]`);
    if (!el) {
      return null;
    }

    return {
      boardLength: parseFloat(el.dataset.boardLength) || 0,
      boardsPerUnit: parseFloat(el.dataset.boardsPerUnit) || 1,
      sqftPerLf: parseFloat(el.dataset.sqftPerLf) || 0,
      soldPerBox: el.dataset.soldPerBox === 'true',
      unitPriceCents: parseFloat(el.dataset.unitPrice) || 0
    };
  }

  // ============================================
  // CART CHANGE: Read a line item's current properties so we never drop them
  // ============================================

  async function getExistingLineItemProperties(lineKey) {
    try {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
      const res = await originalFetch(`${root}cart.js`, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) {
        return null;
      }
      const cart = await res.json();
      const item = (cart.items || []).find(function(i) { return i.key === lineKey; });
      return item ? Object.assign({}, item.properties) : {};
    } catch (e) {
      return null;
    }
  }

  // ============================================
  // FETCH INTERCEPTOR
  // ============================================

  const originalFetch = window.fetch;
  window.fetch = async function(url, options) {
    const urlStr = url ? url.toString() : '';

    // ---- INTERCEPT /cart/add.js ----
    if (urlStr.includes('/cart/add.js') && options && options.body) {
      try {
        let formData = options.body;

        // Handle FormData
        if (formData instanceof FormData) {
          const variantId = formData.get('id');
          const quantity = parseInt(formData.get('quantity'), 10) || 1;

          if (variantId) {
            const variantData = getVariantDataFromProductPage(variantId);

            if (variantData) {
              const sqftString = calculateSqftString(
                quantity,
                variantData.boardLength,
                variantData.boardsPerUnit,
                variantData.sqftPerLf,
                variantData.soldPerBox,
                variantData.unitPriceCents
              );

              if (sqftString) {
                formData.append('properties[Details]', sqftString);
              }

              if (variantData.dimensions) {
                formData.append('properties[Dimensions]', variantData.dimensions);
              }

              if (variantData.partNumber) {
                formData.append('properties[Part #]', variantData.partNumber);
              }
            }
          }
        }
        // Handle JSON body
        else if (typeof formData === 'string') {
          try {
            const jsonBody = JSON.parse(formData);
            const variantId = jsonBody.id || jsonBody.items?.[0]?.id;
            const quantity = jsonBody.quantity || jsonBody.items?.[0]?.quantity || 1;

            if (variantId) {
              const variantData = getVariantDataFromProductPage(variantId);

              if (variantData) {
                const sqftString = calculateSqftString(
                  quantity,
                  variantData.boardLength,
                  variantData.boardsPerUnit,
                  variantData.sqftPerLf,
                  variantData.soldPerBox,
                  variantData.unitPriceCents
                );

                if (sqftString) {
                  if (!jsonBody.properties) jsonBody.properties = {};
                  jsonBody.properties['Details'] = sqftString;
                  options.body = JSON.stringify(jsonBody);
                }

                if (variantData.dimensions) {
                  if (!jsonBody.properties) jsonBody.properties = {};
                  jsonBody.properties['Dimensions'] = variantData.dimensions;
                  options.body = JSON.stringify(jsonBody);
                }

                if (variantData.partNumber) {
                  if (!jsonBody.properties) jsonBody.properties = {};
                  jsonBody.properties['Part #'] = variantData.partNumber;
                  options.body = JSON.stringify(jsonBody);
                }
              }
            }
          } catch (e) {
            // Body was not JSON; nothing to do.
          }
        }
      } catch (error) {
        // Swallow errors so a property failure never blocks add to cart.
      }
    }

    // ---- INTERCEPT /cart/change.js ----
    if (urlStr.includes('/cart/change.js') && options && options.body) {
      try {
        let body;
        let isJson = false;

        if (typeof options.body === 'string') {
          body = JSON.parse(options.body);
          isJson = true;
        } else if (options.body instanceof FormData) {
          body = {
            id: options.body.get('id'),
            quantity: options.body.get('quantity')
          };
        }

        const lineKey = body.id;
        const newQuantity = parseInt(body.quantity, 10);

        // Skip if quantity is not being changed (properties-only update)
        if (body.quantity === undefined) {
          return originalFetch.apply(this, arguments);
        }

        // Get sqft data for this line item from the cart DOM
        const sqftData = getSqftDataFromCart(lineKey);

        if (sqftData && newQuantity > 0) {
          const sqftString = calculateSqftString(
            newQuantity,
            sqftData.boardLength,
            sqftData.boardsPerUnit,
            sqftData.sqftPerLf,
            sqftData.soldPerBox,
            sqftData.unitPriceCents
          );

          if (sqftString && isJson) {
            // We must send a properties object to refresh Details, but Shopify's
            // /cart/change.js REPLACES the line's properties with whatever we send.
            // So read the line's current properties first and resend them all,
            // overriding only Details. This preserves Lead time, Dimensions,
            // Part #, and any other property instead of wiping them.
            const existingProperties = await getExistingLineItemProperties(lineKey);

            if (existingProperties) {
              body.properties = Object.assign({}, existingProperties, { 'Details': sqftString });
              options.body = JSON.stringify(body);
            }
          }
        }
      } catch (error) {
        // Swallow errors so a property failure never blocks a cart change.
      }
    }

    return originalFetch.apply(this, arguments);
  };

})();
